import {
  WheelLockEvidenceSource,
  WheelLockEvidenceSourceLabels,
  WheelLockKeyCondition,
  WheelLockKeyLocationKind,
  WheelLockKeyMatch,
  WheelLockPresence
} from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';

import { getLabelMapFromEntries } from './choiceOptions';

/** Wire value to the label a person reads. Unknown stays a real choice. */
export const WHEEL_LOCK_PRESENCE_LABELS: Record<WheelLockPresence, string> = {
  [WheelLockPresence.UNKNOWN]: 'Unknown',
  [WheelLockPresence.PRESENT]: 'Present',
  [WheelLockPresence.ABSENT]: 'Absent'
};

export const WHEEL_LOCK_KEY_CONDITION_LABELS: Record<WheelLockKeyCondition, string> = {
  [WheelLockKeyCondition.UNKNOWN]: 'Unknown',
  [WheelLockKeyCondition.AVAILABLE]: 'Available',
  [WheelLockKeyCondition.MISSING]: 'Missing',
  [WheelLockKeyCondition.DAMAGED]: 'Damaged'
};

export const WHEEL_LOCK_KEY_LOCATION_LABELS: Record<WheelLockKeyLocationKind, string> = {
  [WheelLockKeyLocationKind.UNKNOWN]: 'Unknown',
  [WheelLockKeyLocationKind.GLOVEBOX]: 'Glovebox',
  [WheelLockKeyLocationKind.BOOT]: 'Boot',
  [WheelLockKeyLocationKind.CUSTOMER]: 'Customer',
  [WheelLockKeyLocationKind.WORKSHOP]: 'Workshop'
};

const WHEEL_LOCK_KEY_MATCH_LABELS: Record<WheelLockKeyMatch, string> = {
  [WheelLockKeyMatch.UNKNOWN]: 'Unknown',
  [WheelLockKeyMatch.CONFIRMED_FOR_SCOPE]: 'Confirmed for this scope',
  [WheelLockKeyMatch.NOT_CONFIRMED]: 'Not confirmed'
};

export const WHEEL_LOCK_SOURCE_LABELS = getLabelMapFromEntries(WheelLockEvidenceSource, WheelLockEvidenceSourceLabels);

const toOptions = <T extends string>(
  labels: Record<T, string>,
  order: readonly T[],
  icons?: Record<T, NoddiIconName>
) => order.map((value) => ({ value, label: labels[value], ...(icons === undefined ? {} : { icon: icons[value] }) }));

const WHEEL_LOCK_PRESENCE_ICONS: Record<WheelLockPresence, NoddiIconName> = {
  [WheelLockPresence.UNKNOWN]: 'CircleHelp',
  [WheelLockPresence.PRESENT]: 'CircleCheck',
  [WheelLockPresence.ABSENT]: 'Ban'
};

const WHEEL_LOCK_KEY_CONDITION_ICONS: Record<WheelLockKeyCondition, NoddiIconName> = {
  [WheelLockKeyCondition.UNKNOWN]: 'CircleHelp',
  [WheelLockKeyCondition.AVAILABLE]: 'Key',
  [WheelLockKeyCondition.MISSING]: 'CircleX',
  [WheelLockKeyCondition.DAMAGED]: 'TriangleAlert'
};

const WHEEL_LOCK_KEY_LOCATION_ICONS: Record<WheelLockKeyLocationKind, NoddiIconName> = {
  [WheelLockKeyLocationKind.UNKNOWN]: 'CircleHelp',
  [WheelLockKeyLocationKind.GLOVEBOX]: 'Archive',
  [WheelLockKeyLocationKind.BOOT]: 'Luggage',
  [WheelLockKeyLocationKind.CUSTOMER]: 'User',
  [WheelLockKeyLocationKind.WORKSHOP]: 'Wrench'
};

export const WHEEL_LOCK_PRESENCE_OPTIONS = toOptions(
  WHEEL_LOCK_PRESENCE_LABELS,
  [WheelLockPresence.UNKNOWN, WheelLockPresence.PRESENT, WheelLockPresence.ABSENT],
  WHEEL_LOCK_PRESENCE_ICONS
);

export const WHEEL_LOCK_KEY_CONDITION_OPTIONS = toOptions(
  WHEEL_LOCK_KEY_CONDITION_LABELS,
  [
    WheelLockKeyCondition.UNKNOWN,
    WheelLockKeyCondition.AVAILABLE,
    WheelLockKeyCondition.MISSING,
    WheelLockKeyCondition.DAMAGED
  ],
  WHEEL_LOCK_KEY_CONDITION_ICONS
);

export const WHEEL_LOCK_KEY_LOCATION_OPTIONS = toOptions(
  WHEEL_LOCK_KEY_LOCATION_LABELS,
  [
    WheelLockKeyLocationKind.UNKNOWN,
    WheelLockKeyLocationKind.GLOVEBOX,
    WheelLockKeyLocationKind.BOOT,
    WheelLockKeyLocationKind.CUSTOMER,
    WheelLockKeyLocationKind.WORKSHOP
  ],
  WHEEL_LOCK_KEY_LOCATION_ICONS
);

export const WHEEL_LOCK_KEY_MATCH_OPTIONS = toOptions(WHEEL_LOCK_KEY_MATCH_LABELS, [
  WheelLockKeyMatch.UNKNOWN,
  WheelLockKeyMatch.CONFIRMED_FOR_SCOPE,
  WheelLockKeyMatch.NOT_CONFIRMED
]);
